// live.tsx — the bridge between the design's screens and the real /v2 API.
// Small shared helpers: the request context from app state, an async-fetch hook, the
// api-key gate that fronts the dashboard, and tiny loading / error / "preview" affordances.
import { useState, useEffect, useCallback, type ReactNode } from 'react';

import { ApiError } from '@/api/client';
import { ME } from '@/api/payouts';

import { useApp, Card, Button, Field, Input, Banner, Icon, Pill } from '@/components/ds';
import { Spinner } from '@/components/code';
import type { AppContextValue } from '@/lib/types';

/** { env, apiKey } passed to every api/payouts function. */
export function useApiCtx(): { env: AppContextValue['env']; apiKey: string } {
  const app = useApp();
  return { env: app.env, apiKey: app.apiKey };
}

/** Self-serve partners address their sole business with the `me` sentinel; trusted partners
 *  use the real org id. */
export function businessIdFor(app: AppContextValue, explicitId?: string | null): string | null {
  if (explicitId) return explicitId;
  return app.partner === 'trusted' ? null : ME;
}

interface AsyncState<T> {
  loading: boolean;
  data: T | null;
  error: unknown | null;
  reload: () => void;
}

interface UseAsyncOptions {
  enabled?: boolean;
}

/** Run an async fn on deps change with cancellation. `enabled:false` skips it. */
export function useAsync<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
  { enabled = true }: UseAsyncOptions = {}
): AsyncState<T> {
  const [state, setState] = useState<{ loading: boolean; data: T | null; error: unknown | null }>({
    loading: enabled,
    data: null,
    error: null,
  });
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  useEffect(() => {
    if (!enabled) {
      setState({ loading: false, data: null, error: null });
      return;
    }
    const ctrl = new AbortController();
    let live = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    Promise.resolve(fn(ctrl.signal))
      .then((data) => {
        if (live) setState({ loading: false, data, error: null });
      })
      .catch((error: unknown) => {
        if (live && (error as { name?: string })?.name !== 'AbortError') setState({ loading: false, data: null, error });
      });
    return () => {
      live = false;
      ctrl.abort();
    };
  }, [...deps, nonce, enabled]);
  return { ...state, reload };
}

interface LoadingProps {
  label?: string;
  pad?: number;
}
export function Loading({ label = 'Loading…', pad = 48 }: LoadingProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: pad, color: 'var(--text-secondary)' }}>
      <Spinner size={18} />
      <span style={{ fontSize: 14 }}>{label}</span>
    </div>
  );
}

interface ErrorBannerProps {
  error: unknown;
  onRetry?: () => void;
}
export function ErrorBanner({ error, onRetry }: ErrorBannerProps) {
  const msg = error instanceof ApiError ? `${error.status} · ${error.message}` : (error as Error)?.message || String(error);
  return (
    <Banner kind="critical" cta={onRetry ? 'Retry' : undefined} onCta={onRetry}>
      <strong>Request failed.</strong> {msg}
    </Banner>
  );
}

interface PreviewBadgeProps {
  children?: ReactNode;
}
/** A small "Preview" tag for screens the design includes but the API can't back yet. */
export function PreviewBadge({ children = 'Preview' }: PreviewBadgeProps) {
  return (
    <Pill kind="warning" dot={false} style={{ marginLeft: 8 }}>
      {children}
    </Pill>
  );
}

interface PreviewNoteProps {
  children?: ReactNode;
}
/** Banner explaining a screen is design-only until the backend ships the endpoint. */
export function PreviewNote({ children }: PreviewNoteProps) {
  return (
    <Banner kind="warning" style={{ marginBottom: 20 }}>
      <strong>Preview.</strong> {children} The data below is illustrative — see the API support TODO.
    </Banner>
  );
}

interface ApiKeyGateProps {
  children?: ReactNode;
}
/**
 * Fronts the dashboard. Until a partner api-key is in memory, the user pastes the key they
 * received at partner creation (we don't persist it — product decision). Once set, children render.
 */
export function ApiKeyGate({ children }: ApiKeyGateProps) {
  const app = useApp();
  const [draft, setDraft] = useState('');
  if (app.hasKey) return <>{children}</>;
  return (
    <div style={{ maxWidth: 460, margin: '64px auto 0', padding: '0 24px' }}>
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 14,
          background: 'var(--accent-soft)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 18,
        }}
      >
        <Icon name="lock" size={26} color="var(--accent)" />
      </div>
      <h2 style={{ margin: 0, fontSize: 22, letterSpacing: '-0.01em' }}>Connect your partner key</h2>
      <p style={{ fontSize: 14.5, lineHeight: '22px', color: 'var(--text-secondary)', margin: '10px 0 20px' }}>
        Paste the API key you received when your partner account was created. It authenticates every call to <strong>{app.envLabel}</strong>{' '}
        and is held in memory for this session only — never stored.
      </p>
      <Field label="Partner API key">
        <Input value={draft} onChange={setDraft} placeholder="melio_secret_production_… / melio_secret_staging01_…" mono />
      </Field>
      <Button
        fullWidth
        size="large"
        style={{ marginTop: 18 }}
        disabled={!draft.trim()}
        rightIcon="arrow-right"
        onClick={() => app.setApiKey(draft.trim())}
      >
        Load my dashboard
      </Button>
      <Banner kind="brand" style={{ marginTop: 16 }}>
        Don't have a key yet? Create a partner from "Get API key". A returning user can re-paste the key here.
      </Banner>
    </div>
  );
}
